import React, { useState, useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import VideoControls from './components/VideoControls';
import { ReactComponent as ApretameIcon } from './assets/icons/apretame.svg';
import { ReactComponent as FlechaIcon } from './assets/icons/flecha.svg';
import './index.css';
import './App.css';

import FacebookIcon from './assets/icons/facebook-circle.svg';
import TiktokIcon from './assets/icons/tiktok-circle.svg';
import SpotifyIcon from './assets/icons/spotify-circle.svg';
import InstagramIcon from './assets/icons/instagram-circle.svg';

// Sin offset — los dos videos arrancan y se sincronizan al mismo tiempo,
// sin desfasaje entre ellos.

// Vimeo a veces no resuelve setCurrentTime cuando el video ya está
// en esa posición exacta (no dispara el evento 'seeked' interno).
// Este helper evita que eso trabe el flujo para siempre: si no resuelve
// en 1.2s, seguimos igual.
const seekWithTimeout = (player, time, ms = 1200) => {
  return Promise.race([
    player.setCurrentTime(time),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
};

// Fases: 'cover' → 'transition' → 'revealing' → 'playing'
function App() {
  const [gamePhase, setGamePhase]       = useState('cover');
  const [viewMode, setViewMode]         = useState('v1'); // 'v1' | 'v2'
  const [volume, setVolume]             = useState(0.5);
  const [muted, setMuted]               = useState(false);
  const [playing, setPlaying]           = useState(false);
  const [isSwitching, setIsSwitching]   = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError]     = useState(false);
  const [hasStarted, setHasStarted]     = useState(false);
  const [playersReady, setPlayersReady] = useState(false);
  const [showHint, setShowHint]       = useState(false); // cartelito junto a APRETAME

  const iframe1Ref       = useRef(null);
  const iframe2Ref       = useRef(null);
  const player1Ref       = useRef(null);
  const player2Ref       = useRef(null);
  const controlsTimerRef = useRef(null);
  const pantallaTvRef    = useRef(null);
  const mutedRef         = useRef(muted);
  const volumeRef        = useRef(volume);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // ─── CARTELITO DE AYUDA — aparece al arrancar el video, se va a los 6s ──────
  useEffect(() => {
    if (gamePhase !== 'playing') return;
    setShowHint(true);
    const t = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(t);
  }, [gamePhase]);

  // ─── 1. INICIALIZACIÓN — autoplay MUTE detrás de la portada ─────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const p1 = new Player(iframe1Ref.current);
        const p2 = new Player(iframe2Ref.current);

        p1.on('error', () => setVideoError(true));
        p2.on('error', () => setVideoError(true));

        await Promise.all([p1.ready(), p2.ready()]);
        if (cancelled) return;

        await Promise.all([p1.setVolume(0), p2.setVolume(0)]);

        await Promise.all([p1.play(), p2.play()]);
        if (cancelled) return;

        player1Ref.current = p1;
        player2Ref.current = p2;
        setPlayersReady(true);

        // ── Al terminar el video (master = video 2), volver a la portada ──
        // Reseteamos todo y volvemos a arrancar el autoplay mute detrás
        // de la portada, listo para el próximo espectador.
        p2.on('ended', async () => {
          try {
            await Promise.all([p1.pause(), p2.pause()]);
            await Promise.all([
              seekWithTimeout(p1, 0),
              seekWithTimeout(p2, 0),
            ]);
            setViewMode('v1');
            setPlaying(false);
            setHasStarted(false);
            setGamePhase('cover');

            // Volver a arrancar mute detrás de la nueva portada
            await Promise.all([p1.setVolume(0), p2.setVolume(0)]);
            await Promise.all([p1.play(), p2.play()]);
          } catch (e) {
            console.error('Error al reiniciar tras el fin del video:', e);
          }
        });
      } catch (e) {
        console.error('Error init:', e);
        setVideoError(true);
      }
    };

    init();

    return () => {
      cancelled = true;
      player1Ref.current?.destroy();
      player2Ref.current?.destroy();
      player1Ref.current = null;
      player2Ref.current = null;
    };
  }, []);

  // ─── 2. PLAY / PAUSE (post-click, desde los controles) ──────────────────────
  useEffect(() => {
    if (!player1Ref.current || !player2Ref.current || !hasStarted) return;
    if (playing) {
      Promise.all([
        player1Ref.current.play(),
        player2Ref.current.play(),
      ]).catch(console.warn);
    } else {
      Promise.all([
        player1Ref.current.pause(),
        player2Ref.current.pause(),
      ]).catch(console.warn);
    }
  }, [playing, hasStarted]);

  // ─── 3. VOLUMEN — audio SIEMPRE en video 2, video 1 siempre mute ────────────
  useEffect(() => {
    if (!player1Ref.current || !player2Ref.current || !hasStarted) return;
    const vol = muted ? 0 : volume;
    player1Ref.current.setVolume(0).catch(console.warn);
    player2Ref.current.setVolume(vol).catch(console.warn);
  }, [volume, muted, hasStarted]);

  // ─── 4. SINCRONIZACIÓN — sin offset, los dos al mismo tiempo ────────────────
  useEffect(() => {
    if (!playersReady) return;
    const interval = setInterval(async () => {
      if (!player1Ref.current || !player2Ref.current) return;
      try {
        const [t1, t2] = await Promise.all([
          player1Ref.current.getCurrentTime(),
          player2Ref.current.getCurrentTime(),
        ]);
        // Sin offset: si se desincronizan más de 0.5s, corregimos v1 al tiempo de v2
        if (Math.abs(t1 - t2) > 0.5) {
          await seekWithTimeout(player1Ref.current, t2);
        }
      } catch (e) {
        console.warn('Sync error:', e);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [playersReady]);

  // ─── 5. PRESS START — click en la portada ───────────────────────────────────
  const handlePressStart = () => {
    if (!playersReady || videoError) return;

    setGamePhase('transition');

    setTimeout(async () => {
      setGamePhase('revealing');

      try {
        // Sin offset — los dos videos vuelven a 0 juntos
        await Promise.all([
          seekWithTimeout(player1Ref.current, 0),
          seekWithTimeout(player2Ref.current, 0),
        ]);

        player1Ref.current.setVolume(0).catch(console.warn);
        player2Ref.current.setVolume(mutedRef.current ? 0 : volumeRef.current).catch(console.warn);
        setPlaying(true);
        setHasStarted(true);

        setGamePhase('playing');
      } catch (e) {
        console.error('Error activando audio:', e);
        setGamePhase('playing');
      }
    }, 800);
  };

  // ─── 6. BOTÓN APRETAME ──────────────────────────────────────────────────────
  const handleApretame = () => {
    if (isSwitching || videoError || gamePhase !== 'playing') return;
    setShowHint(false);

    setIsSwitching(true);
    setTimeout(() => {
      setViewMode(prev => prev === 'v1' ? 'v2' : 'v1');
      setIsSwitching(false);
    }, 300);
  };

  // ─── 7. VISIBILIDAD DE PESTAÑA ──────────────────────────────────────────────
  useEffect(() => {
    let resumeTimeout;
    const handler = () => {
      if (!hasStarted) return;
      if (document.hidden) {
        clearTimeout(resumeTimeout);
        setPlaying(false);
      } else {
        resumeTimeout = setTimeout(() => setPlaying(true), 400);
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      clearTimeout(resumeTimeout);
    };
  }, [hasStarted]);

  // ─── 8. AUTO-OCULTAR CONTROLES ──────────────────────────────────────────────
  useEffect(() => {
    const show = () => {
      setShowControls(true);
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    };
    const el = pantallaTvRef.current;
    if (!el) return;
    el.addEventListener('mousemove',  show, { capture: true });
    el.addEventListener('touchstart', show, { capture: true, passive: true });
    el.addEventListener('click',      show, { capture: true });
    show();
    return () => {
      clearTimeout(controlsTimerRef.current);
      el.removeEventListener('mousemove',  show, { capture: true });
      el.removeEventListener('touchstart', show, { capture: true });
      el.removeEventListener('click',      show, { capture: true });
    };
  }, []);

  const toggleMute      = () => setMuted(prev => !prev);
  const togglePlayPause = () => setPlaying(prev => !prev);

  const toggleFullscreen = () => {
    const el = pantallaTvRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => {
        window.screen.orientation?.lock('landscape').catch(() => {});
      }).catch(console.warn);
    } else {
      document.exitFullscreen?.().then(() => {
        window.screen.orientation?.unlock?.();
      }).catch(console.warn);
    }
  };

  const shareOnFacebook = () => {
    const url  = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('🎵 ¡Mirá este increíble video clip interactivo de MANDARINA! 🍊');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank', 'width=600,height=400,menubar=no,toolbar=no');
  };

  const shareOnWhatsApp = () => {
    const url  = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('🎵 ¡Mirá este increíble video clip interactivo de MANDARINA! 🍊 ');
    window.open(`https://wa.me/?text=${text}${url}`, '_blank', 'width=600,height=400,menubar=no,toolbar=no');
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="app-container">
      <div className="banner-container">
        <img src="/images/isologoChasky.jpg" alt="Chasky" className="banner-logo" />
      </div>

      <div className="header-title">EL CHASKY PUM presenta: MANDARINAS</div>
      <div className="header-subtitle">Elija su propia aventura</div>

      {videoError && (
        <div className="video-error-message">
          Error al cargar los videos. Por favor recargá la página.
        </div>
      )}

      <div className="reproductor-wrapper">
        <div ref={pantallaTvRef} className="pantalla-tv">

          {(gamePhase === 'cover' || gamePhase === 'transition') && (
            <div className={`cover-overlay ${gamePhase === 'transition' ? 'cover-tv-off' : ''}`}>
              <img
                src="/images/Chaski-front.jpg"
                alt="El Chasky Pum — MANDARINAS"
                className="cover-bg-image"
              />
              <div className="cover-scanlines" />
              <div className="cover-content">
                <p className="press-start-label">
                  {playersReady ? 'HAGA CLIC EN EL BOTÓN PARA CAMBIAR DE VIDEO' : 'CARGANDO...'}
                </p>
                <button
                  className={`boton-apretame-independiente boton-apretame-cover ${isSwitching ? 'cambiando' : ''}`}
                  onClick={handlePressStart}
                  disabled={!playersReady || videoError}
                  aria-label="Iniciar video"
                >
                  <span className="coin-shine" aria-hidden="true" />
                  <ApretameIcon className="icono-apretame" />
                </button>
              </div>
            </div>
          )}

          {gamePhase === 'transition' && (
            <div className="tv-turn-on-anim" />
          )}

          <div className={`black-mask ${gamePhase === 'playing' ? 'black-mask-hidden' : ''}`} />

          <iframe
            ref={iframe1Ref}
            src="https://player.vimeo.com/video/1126450439?muted=1&controls=0&autopause=0"
            className="video-frame"
            style={{ opacity: viewMode === 'v1' ? 1 : 0, zIndex: viewMode === 'v1' ? 2 : 1 }}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
            title="Video 1 — Fabián Show"
          />

          <iframe
            ref={iframe2Ref}
            src="https://player.vimeo.com/video/1107167159?muted=1&controls=0&autopause=0"
            className="video-frame"
            style={{ opacity: viewMode === 'v2' ? 1 : 0, zIndex: viewMode === 'v2' ? 2 : 1 }}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
            title="Video 2 — Fiesta Terraza"
          />

          {gamePhase === 'playing' && (
            <>
              {showHint && (
                <div className="apretame-hint" aria-hidden="true">
                  <span className="apretame-hint-text">Apretame otra vez para cambiar de video</span>
                  <FlechaIcon className="apretame-hint-arrow" />
                </div>
              )}

              <button
                className={`boton-apretame-independiente boton-apretame-playing ${isSwitching ? 'cambiando' : ''}`}
                onClick={handleApretame}
                disabled={videoError}
                aria-label="Cambiar cámara"
              >
                <ApretameIcon className="icono-apretame" />
              </button>

              <VideoControls
                isPlaying={playing}
                onPlayPause={togglePlayPause}
                isMuted={muted}
                onMuteToggle={toggleMute}
                onVolumeChange={setVolume}
                volume={volume}
                onFullScreen={toggleFullscreen}
                disableButtons={videoError}
                isVisible={showControls}
              />
            </>
          )}
        </div>
      </div>

      <div className="video-footer-title">VIDEO CLIP INTERACTIVO</div>
      <div className="video-legend">
        <span className="video-legend-item">1: Estudio Fabián Show</span>
        <span className="video-legend-item">2: Fiesta en la Terraza</span>
      </div>
      <p className="video-explain">Juegue con el botón en cualquier momento para cambiar al video que más le guste.</p>

      <div className="share-buttons-container">
        <button className="share-button facebook-share" onClick={shareOnFacebook}>
          COMPARTIR EN<br />FACEBOOK
        </button>
        <button className="share-button whatsapp-share" onClick={shareOnWhatsApp}>
          COMPARTIR POR<br />WHATSAPP
        </button>
      </div>

      <div className="social-icons-container">
        <a href="https://facebook.com/elchaskypum" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
          <img src={FacebookIcon} alt="Facebook" />
        </a>
        <a href="https://tiktok.com/@elchaskypum" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="TikTok">
          <img src={TiktokIcon} alt="TikTok" />
        </a>
        <a href="https://open.spotify.com/artist/1Lzm6vF2W38Y1uZ8cP5g4v" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Spotify">
          <img src={SpotifyIcon} alt="Spotify" />
        </a>
        <a href="https://instagram.com/elchaskypum" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
          <img src={InstagramIcon} alt="Instagram" />
        </a>
      </div>

      <div className="footer-chasky">
        <p className="footer-copyright">
          © 2026 El Chasky Pum · Todos los derechos reservados
        </p>
        <p className="footer-dev">
          Interactive Experience by <span>Indigo XP</span>
        </p>
      </div>

    </div>
  );
}

export default App;